/**
 * Guardians API functions + TanStack Query hooks.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api } from '@core/api/client';
import type { components } from '@api/index';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GuardianResponse = components['schemas']['GuardianResponse'];
export type PaginatedGuardians = components['schemas']['PaginatedResult_GuardianResponse_'];

export interface GuardianCreate {
  first_name: string;
  last_name: string;
  email?: string | null;
  phone: string;
  relationship?: 'mother' | 'father' | 'guardian' | 'other';
  is_primary?: boolean;
  consent_given?: boolean;
  student_ids?: string[];
}

export interface GuardianUpdate {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string;
  relationship?: 'mother' | 'father' | 'guardian' | 'other';
  is_primary?: boolean;
  consent_given?: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const guardianKeys = {
  all: ['guardians'] as const,
  list: (params: { limit?: number } = {}) => ['guardians', 'list', params] as const,
  detail: (id: string) => ['guardians', 'detail', id] as const,
  byPhone: (phone: string) => ['guardians', 'by-phone', phone] as const,
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function listGuardians(params?: {
  cursor?: string;
  limit?: number;
}): Promise<PaginatedGuardians> {
  return api.get<PaginatedGuardians>('/v1/guardians', {
    query: { cursor: params?.cursor, limit: params?.limit ?? 20 },
  });
}

export async function getGuardian(id: string): Promise<GuardianResponse> {
  return api.get<GuardianResponse>(`/v1/guardians/${id}`);
}

export async function getGuardianByPhone(phone: string): Promise<GuardianResponse> {
  return api.get<GuardianResponse>(`/v1/guardians/by-phone/${encodeURIComponent(phone)}`);
}

export async function createGuardian(body: GuardianCreate): Promise<GuardianResponse> {
  return api.post<GuardianResponse>('/v1/guardians', body);
}

export async function updateGuardian(
  id: string,
  body: GuardianUpdate,
): Promise<GuardianResponse> {
  return api.patch<GuardianResponse>(`/v1/guardians/${id}`, body);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useGuardians(params: { limit?: number } = {}) {
  return useInfiniteQuery<
    PaginatedGuardians,
    Error,
    InfiniteData<PaginatedGuardians>,
    ReturnType<typeof guardianKeys.list>,
    string | undefined
  >({
    queryKey: guardianKeys.list(params),
    queryFn: ({ pageParam }) => listGuardians({ ...params, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

export function useGuardian(id: string) {
  return useQuery({
    queryKey: guardianKeys.detail(id),
    queryFn: () => getGuardian(id),
    enabled: Boolean(id),
  });
}

export function useGuardianByPhone(phone: string) {
  return useQuery({
    queryKey: guardianKeys.byPhone(phone),
    queryFn: () => getGuardianByPhone(phone),
    enabled: Boolean(phone),
  });
}

export function useCreateGuardian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createGuardian,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: guardianKeys.all });
    },
  });
}

export function useUpdateGuardian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GuardianUpdate }) =>
      updateGuardian(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: guardianKeys.all });
      qc.invalidateQueries({ queryKey: guardianKeys.detail(id) });
    },
  });
}

